import { colors } from '@lmc/common/constants'
import type {
  Carer,
  Home,
  LogCategoryItem,
  LogResponse,
  Resident
} from '@lmc/ts-common'
import { AvatarLabel, Text } from '@lmc/ui-kit'
import useGoalsAccess from 'components/AccessControl/features/useGoalsAccess'
import { Gallery, GalleryItem } from 'components/Gallery'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import useGoalsIDs from 'screens/Residents/screens/ResidentsGoalsScreen/components/useGoals/useGoalsIDs'
import styled from 'styled-components'
import { getResidentPronouns } from '../../../../common/utils'
import { NAMESPACE } from '../../../../locale'
import { logsConstants } from '../../../../locale/logs'
import { history } from '../../../../store'
import { COMPLETED_BY_KEYS } from '../../utils/completedByKeys'
import { isSeizureItemLog } from '../../utils/logUtils'
import LogLinkedGoals from './LogLinkedGoals'
import LogMeasurements from './LogMeasurements'
import LogRevisions from './LogRevisions'
import LogViewFooter, { Section, SectionTitle } from './LogViewFooter'
import PainChekAssessment from './PainChekAssessment'

// @ts-ignore
interface LogWithRevisions extends LogResponse {
  logRevisions?: LogResponse[]
  externalId?: string
  picture1?: string
  picture2?: string
  picture3?: string
  editedOn?: string
  editedAt?: string
  reasonCode?: string
}

interface Props {
  log: LogWithRevisions
  resident: Resident
  item: LogCategoryItem
  loggedBy: Carer
  witnessedBy: Carer
  editedBy: Carer
  features: Home
  painChekAssessmentResult?: React.ComponentProps<
    typeof PainChekAssessment
  >['assessmentResult']
  testId?: string
}

export const LogDetailsV2 = ({
  log,
  resident,
  item,
  loggedBy,
  witnessedBy,
  editedBy,
  features,
  painChekAssessmentResult,
  testId
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { t: tLogs } = useTranslation(NAMESPACE.LOGS)
  const renderResidentAvatar = () => {
    if (!resident) return null
    const pic = resident.picture as unknown as string
    return (
      <div
        data-testid={testId ? `${testId}-resident-profile` : 'resident-profile'}
      >
        <AvatarLabel
          onSelect={() => history.push(`/residents/${resident?.id}/profile`)}
          label={`${resident.name.first} ${resident.name.last}`}
          picture={pic}
          testId={testId ? `${testId}-resident-avatar` : 'resident-avatar'}
        />
      </div>
    )
  }

  const renderImages = () => {
    const images = [log.picture1, log.picture2, log.picture3]
    const { videoFile } = log

    const validImages = images.filter((url) => url)

    const shouldRender: boolean = Boolean(videoFile || validImages.length)

    if (!shouldRender) {
      return null
    }

    return (
      <TitledBlock
        data-testid={
          testId ? `${testId}-attached-media-section` : 'attached-media-section'
        }
      >
        <SectionTitle>{t('components.logEditForm.attachedMedia')}</SectionTitle>
        <MediaWrapper>
          {Boolean(validImages.length) && (
            <Thumbnails>
              <Gallery>
                {validImages.map((url, index) => (
                  <GalleryItem
                    key={`image-${index + 1}`}
                    src={url || ''}
                    alt={tLogs(logsConstants.LOG_VIEW_PHOTO_ALT)}
                    style={styles.thumbnail}
                    width={749}
                    height={1000}
                  />
                ))}
              </Gallery>
            </Thumbnails>
          )}
          {Boolean(videoFile) && (
            <Video controls>
              <source src={videoFile} type="video/mp4" />
              <track
                src="captions_en.vtt"
                kind="captions"
                label="english_captions"
              />
            </Video>
          )}
        </MediaWrapper>
      </TitledBlock>
    )
  }

  const renderMeasurements = (renderLink?: boolean) => {
    if (item && (item.amountOneKey || item.amountTwoKey)) {
      return (
        <LogMeasurements
          log={log}
          item={item}
          onClick={(url: string) => history.push(url)}
          renderLink={renderLink}
        />
      )
    }

    return null
  }

  const hasGoals = useGoalsAccess()
  const { filteredGoals: goals } = useGoalsIDs({
    goalsIDs: log.goals as string[],
    residentID: resident?.id
  })

  const showLinkOnly = isSeizureItemLog(item?.id)

  const completedByText =
    log.doneBySu && resident
      ? tLogs(COMPLETED_BY_KEYS[getResidentPronouns(resident).subject], {
          name: `${resident.name.first} ${resident.name.last}`
        })
      : undefined

  return (
    <div data-testid={testId ? `${testId}-details` : 'log-details'}>
      <Card>
        <ResidentSection>{renderResidentAvatar()}</ResidentSection>
        <DescriptionSection
          data-testid={
            testId ? `${testId}-description-section` : 'log-description-section'
          }
        >
          <SectionTitle as="h2">
            {t('components.logEditForm.description')}
          </SectionTitle>
          <Text
            as="p2"
            style={styles.descriptionText}
            data-testid={
              testId ? `${testId}-description-text` : 'log-description-text'
            }
          >
            {log.description}
          </Text>
          {witnessedBy && (
            <Text
              as="p2"
              style={styles.descriptionText}
              data-testid={
                testId
                  ? `${testId}-description-witnessed-by`
                  : 'log-description-witnessed-by'
              }
            >
              {tLogs(logsConstants.LOG_VIEW_WITNESSED_BY_NAME, {
                name: `${witnessedBy.name.first} ${witnessedBy.name.last}`
              })}
            </Text>
          )}
          <Measurements>{renderMeasurements(showLinkOnly)}</Measurements>
          {log.reasonCode ? (
            <div
              data-testid={
                testId ? `${testId}-outcome-section` : 'outcome-section'
              }
            >
              <Text as="p2" style={styles.title}>
                {`${tLogs(logsConstants.LABEL_WITH_COLON, {
                  label: tLogs(logsConstants.LOG_OUTCOME)
                })} `}
              </Text>
              <Text
                as="p2"
                style={styles.descriptionText}
                data-testid={
                  testId ? `${testId}-outcome-value` : 'outcome-value'
                }
              >
                {log.reasonCode}
              </Text>
            </div>
          ) : null}
        </DescriptionSection>
        <LogViewFooter
          isNextHandover={log.isNextHandover}
          completedByText={completedByText}
          timeLogged={log.timeLogged}
          editedOn={log.editedAt ?? log.editedOn}
          loggedBy={loggedBy}
          witnessedBy={witnessedBy}
          editedBy={editedBy}
          showLocation={Boolean(features.features?.geoLocation?.enabled)}
          latitude={log.latitude}
          longitude={log.longitude}
          accuracy={log.accuracy}
          media={renderImages()}
          goals={
            hasGoals && goals?.length ? (
              <LogLinkedGoals
                goals={goals}
                residentId={resident?.id}
                testId={testId ? `${testId}-goals` : undefined}
              />
            ) : undefined
          }
          testId={testId ? `${testId}-footer` : undefined}
        />
        {log.logRevisions?.length ? (
          <Section
            data-testid={
              testId
                ? `${testId}-log-revisions-section`
                : 'log-revisions-section'
            }
          >
            <SectionTitle>
              {t('components.logEditForm.logRevisions')}
            </SectionTitle>
            <LogRevisions log={log} />
          </Section>
        ) : null}
      </Card>
      {painChekAssessmentResult && (
        <PainChekAssessment assessmentResult={painChekAssessmentResult} />
      )}
    </div>
  )
}

const styles = {
  thumbnail: {
    borderRadius: 12,
    cursor: 'pointer',
    height: 56,
    objectFit: 'cover',
    width: 56
  } as React.CSSProperties,
  descriptionText: {
    fontWeight: 400,
    lineHeight: '24px',
    whiteSpace: 'pre-wrap'
  } as React.CSSProperties,
  title: { fontWeight: 'bold' }
}

export const Card = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  margin-bottom: 32px;
  overflow: hidden;
`

export const ResidentSection = styled(Section)`
  border-top: none;
  font-weight: 600;
  padding-bottom: 0;

  &:empty {
    display: none;
  }
`

export const DescriptionSection = styled(Section)`
  border-top: none;
  gap: 8px;
`

export const TitledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Measurements = styled.div`
  font-size: 14px;
  font-weight: 400;

  strong {
    color: ${colors.black};
    font-size: 14px;
    font-weight: 600;
  }

  > div {
    padding-top: 0 !important;
  }

  button {
    height: auto;
    margin-top: 4px;
    min-height: 0;
    padding: 0;
  }

  &:empty {
    display: none;
  }
`

const Thumbnails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const Video = styled.video`
  border-radius: 12px;
  height: 135px;
  width: 240px;
`

const MediaWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  cursor: pointer;
`

export default LogDetailsV2
